import type { FlowBuilderTurnFailure } from '../../types/flowBuilder/flowBuilderState';
import type {
  FlowBuilderIssueSeverity,
  FlowBuilderTurnErrorCode,
} from '../../types/flowBuilder/flowBuilderStreamEvents';

/** Tools do harness (plano §3). Tool nova sem rótulo cai no fallback com o nome cru. */
const TOOL_LABELS: Record<string, string> = {
  GenerateFlowViaDsl: 'Montando o fluxo',
  PatchBlueprint: 'Ajustando o fluxo',
  AskUser: 'Preparando perguntas',
  LoadSkill: 'Consultando um guia',
  SearchNodeRegistry: 'Consultando os blocos disponíveis',
  GetMultiAgentKnowledge: 'Consultando o guia de multiagente',
  ReadFlow: 'Lendo o fluxo',
  GetNodeDetails: 'Lendo os detalhes de um bloco',
  WriteAgentInstructions: 'Escrevendo as instruções do agente',
  SimulateConversation: 'Simulando uma conversa',
};

export function flowBuilderToolLabel(tool: string): string {
  return TOOL_LABELS[tool] ?? `Executando ${tool}`;
}

/**
 * O `satisfies` cobra rótulo para todo código conhecido; a anotação `Partial` é para a leitura: o
 * contrato SSE é aditivo, e código novo do core pode chegar antes do rótulo — cai na `message`.
 */
const TURN_ERROR_LABELS: Partial<Record<string, string>> = {
  turn_interrupted: 'O processamento parou no meio. Você pode reenviar a mensagem.',
  turn_expired: 'O turno expirou antes de começar.',
  turn_cancelled: 'Geração interrompida.',
  cost_cap_reached: 'O limite de custo do FlowBuilder foi atingido.',
  wall_time_exceeded: 'O turno passou do tempo máximo e foi encerrado.',
  llm_unavailable: 'O modelo de IA está indisponível agora. Tente de novo em instantes.',
  internal_error: 'O FlowBuilder encontrou um erro interno.',
} satisfies Record<FlowBuilderTurnErrorCode, string>;

export function flowBuilderFailureTitle(failure: FlowBuilderTurnFailure): string {
  if (failure.source === 'turn') return TURN_ERROR_LABELS[failure.code] ?? failure.message;
  return failure.message;
}

/** `turn_cancelled` é recuperável no core, mas foi o próprio usuário quem parou: não oferece reenvio. */
export function canResendAfterFailure(failure: FlowBuilderTurnFailure): boolean {
  if (failure.source !== 'turn' || failure.code === 'turn_cancelled') return false;
  return failure.recoverable || failure.code === 'turn_interrupted';
}

/**
 * O POST de turno responde 409 por dois motivos: turno vivo no agente ou revisão do rascunho
 * defasada. Só o segundo se resolve recarregando o rascunho, e quem os separa é o `error.code`.
 */
const DRAFT_REVISION_CONFLICT_CODE = 'DRAFT_REVISION_CONFLICT';

export function isDraftRevisionConflict(failure: FlowBuilderTurnFailure): boolean {
  return (
    failure.source === 'request' &&
    failure.status === 409 &&
    failure.code === DRAFT_REVISION_CONFLICT_CODE
  );
}

export const FLOW_BUILDER_ISSUE_SEVERITY_LABELS: Record<FlowBuilderIssueSeverity, string> = {
  invariant: 'Obrigatório',
  guidance: 'Sugestão',
};

/** O status do runtime fala a língua da implementação; o usuário lê "fluxo", não "DSL". */
const STATUS_REPLACEMENTS: Array<[RegExp, string]> = [
  [/\bDSL\(s\)/gi, 'fluxo(s)'],
  [/\bDSLs\b/gi, 'fluxos'],
  [/\bDSL\b/gi, 'fluxo'],
  [/\bblueprint\b/gi, 'estrutura'],
  [/\binterpretando\b/gi, 'montando'],
  [/\bvalidando\b/gi, 'revisando'],
  [/\bpayload\b/gi, 'dados'],
];

export function humanizeFlowBuilderStatus(raw: string): string | null {
  const text = STATUS_REPLACEMENTS.reduce(
    (current, [pattern, replacement]) => current.replace(pattern, replacement),
    raw,
  ).trim();
  return text || null;
}
